"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ensureTrailingSlash = exports.readableDeviceName = exports.pluralReadableTypeName = exports.readableTypeName = exports.toTitleCase = exports.camelCase_to_Sentence = exports.pluralize = exports.toCamelCase = exports.capitalize = exports.noSpace = exports.camelToDisplay = void 0;
const tslib_1 = require("tslib");
const config_1 = tslib_1.__importDefault(require("@iotv/config"));
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const standardReplacements_1 = require("./standardReplacements");
const debug = !process.env.AWS_SESSION_TOKEN && false;
const camelRegex = new RegExp(/([A-Z])(?=[a-z])/g);
const firstChr = /^[a-z]/u;
const camelToDisplay = (s) => {
    let regexRes = null;
    let display = '';
    let lastIdx = 0;
    do {
        regexRes = camelRegex.exec(s);
        const nextIdx = regexRes?.index ?? s.length;
        const newSub = s.substring(lastIdx, nextIdx);
        debug && console.log('To Camel', { newSub, nextIdx, lastIdx });
        display += `${lastIdx ? ' ' : ''}${newSub}`;
        lastIdx = nextIdx;
    } while (regexRes);
    return display.replace(firstChr, (x) => x.toUpperCase());
};
exports.camelToDisplay = camelToDisplay;
const noSpace = (s) => s.replace(/( |&|\/)/g, '');
exports.noSpace = noSpace;
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
exports.capitalize = capitalize;
const toCamelCase = (attName) => attName
    .split(' ')
    .map((g, i) => {
    g = (i === 0 ? g.charAt(0).toLowerCase() : g.charAt(0).toUpperCase()) + g.slice(1);
    return g;
})
    .join('');
exports.toCamelCase = toCamelCase;
/**
 * Converts a noun into its plural form
 *
 * Use the optional count to auto pluralize and keep horrible code out of components
 */
const pluralize = (str, count) => {
    const plural = pluralNoun(str);
    let res = str;
    if ((0, iotv_v3_types_1.isNonNaNNumber)(count)) {
        if (count > 1 || count === 0) {
            res = plural;
        }
    }
    else {
        res = plural;
    }
    return res;
};
exports.pluralize = pluralize;
/**
 * Converts a noun into its plural form
 *
 * There are still several issues and cases it can't handle
 *
 * https://byjus.com/english/plural-noun/
 * @param noun word to pluralize
 * @returns plural
 */
const pluralNoun = (noun) => {
    const vowels = ['a', 'e', 'i', 'o', 'u'];
    // nnnnXn
    const precedingLastCharacter = noun.substring(noun.length - 2, noun.length - 1);
    // nnnnXX
    const lastTwoCharacters = noun.substring(noun.length - 2);
    // nnnnnX
    const lastCharacter = noun.substring(noun.length - 1);
    // if the word ends in `is` — replace with `es`
    if (lastTwoCharacters === 'is')
        return noun.replace(/is$/, 'es');
    // if the word ends in `us` — replace with `i`
    if (lastTwoCharacters === 'us')
        return noun.replace(/us$/, 'i');
    // if the word ends in `y` preceded with a vowel — add `s`
    if (lastCharacter === 'y' && vowels.includes(precedingLastCharacter))
        return noun + 's';
    // if the word ends in `y` preceded with a consonant — replace with `ies`
    if (lastCharacter === 'y' && !vowels.includes(precedingLastCharacter))
        return noun.replace(/y$/, 'ies');
    // if the word ends in `o` — add `es`
    if (lastCharacter === 'o')
        return noun + 'es';
    // if the word ends in `f` or `fe` — replace with `ves`
    if (lastCharacter === 'f' || lastTwoCharacters === 'fe')
        return noun.replace(/f$|fe$/, 'ves');
    // if the word ends in `x` or `s` — add `es`
    if (lastCharacter === 'x' || lastCharacter === 's')
        return noun + 'es';
    // default rule / fallback — add `s`
    return noun + 's';
};
/**
 * https://stackoverflow.com/a/65705298
 * Formats camelCase to Sentence case with spaces (or lower case)
 *
 * @param text to transform
 * @param capitaliseFirstWord whether the first word should be capitalised (default: `true`)
 * @returns transformed text
 */
const camelCase_to_Sentence = (text, capitaliseFirstWord = true) => {
    return (text ?? '')
        .split(/([A-Z]|\d)/)
        .map((value, index, array) => {
        // If first block then capitalise first letter regardless
        if (!index && capitaliseFirstWord)
            return value.charAt(0).toUpperCase() + value.slice(1);
        // Skip empty blocks
        if (!value)
            return value;
        // Underscore substitution
        if (value === '_')
            return ' ';
        // We have a capital or number
        if (value.length === 1 && value === value.toUpperCase()) {
            const previousCapital = !array[index - 1] || array[index - 1] === '_';
            const nextWord = index + 1 < array.length && array[index + 1] && array[index + 1] !== '_';
            const nextTwoCapitalsOrEndOfString = index + 3 > array.length || (!array[index + 1] && !array[index + 3]);
            // Insert space
            if (!previousCapital || nextWord)
                value = ' ' + value;
            // Start of word or single letter word
            if (nextWord || (!previousCapital && !nextTwoCapitalsOrEndOfString))
                value = value.toLowerCase();
        }
        return value;
    })
        .join('')
        .trim();
};
exports.camelCase_to_Sentence = camelCase_to_Sentence;
/**
 * Adapted from https://stackoverflow.com/a/53952925
 * @param string to convert to Title Case
 * @returns Title Case
 */
const toTitleCase = (string, options) => {
    let s = (string ?? '');
    s = s.replace(/([a-z])([A-Z])/g, '$1 $2'); // Splits camelCase words into separate words
    if (options?.underscoreToSpace) {
        s = s.replace(/_/g, ' '); // Replaces underscores with spaces if the option is set
    }
    s = s.replace(/[-_]+|[^\p{L}\p{N}\s]/gu, ''); // Removes dashes, underscores, and special characters
    s = s.toLowerCase(); // Converts the entire string to lowercase
    s = s.replace(/(^|\s)(\p{L})/gu, (_, space, letter) => space + letter.toUpperCase()) // Capitalizes the first letter of each word
        // replaces standard replacements like 'rssi' with 'RSSI'
        .replace(/\b\w+\b/g, (word) => standardReplacements_1.standardReplacements[word.toLowerCase()] ?? word);
    return s;
};
exports.toTitleCase = toTitleCase;
/**
 * Transforms an object type into something human-readable
 *
 * Chains together:
 * - `groupedObjectTypes[objectTypeId].collectionName` from {@link config.app.webUI.groupedObjectTypes | `config`}
 * - {@link camelCase_to_Sentence | `camelCase_to_Sentence()`}
 * - {@link toTitleCase | `toTitleCase()`}
 */
const readableTypeName = (objectTypeId) => {
    if (objectTypeId === undefined)
        return '';
    const groupedName = config_1.default.app?.webUI?.groupedObjectTypes?.[objectTypeId]?.collectionName;
    return groupedName ?? (0, exports.toTitleCase)((0, exports.camelCase_to_Sentence)(objectTypeId));
};
exports.readableTypeName = readableTypeName;
/**
 * Transforms an object type into something human-readable
 *
 * Chains together:
 * - `groupedObjectTypes[objectTypeId].pluralCollectionName` from {@link config.app.webUI.groupedObjectTypes | `config`}
 * - {@link camelCase_to_Sentence | `camelCase_to_Sentence()`}
 * - {@link toTitleCase | `toTitleCase()`}
 * - {@link pluralize | `pluralize()`}
 */
const pluralReadableTypeName = (objectTypeId) => {
    if (objectTypeId === undefined)
        return '';
    const groupedName = config_1.default.app?.webUI?.groupedObjectTypes?.[objectTypeId]?.pluralCollectionName;
    return groupedName ?? (0, exports.pluralize)((0, exports.toTitleCase)((0, exports.camelCase_to_Sentence)(objectTypeId)));
};
exports.pluralReadableTypeName = pluralReadableTypeName;
const readableDeviceName = (deviceType) => {
    const configuredName = config_1.default.app?.webUI?.deviceMap?.[deviceType]?.name;
    return configuredName ?? iotv_v3_types_1.DeviceTypeNames[deviceType] ?? `0x${deviceType.toString(16)}`;
};
exports.readableDeviceName = readableDeviceName;
const ensureTrailingSlash = (str) => (/\/$/.test(str) ? str : `${str}/`);
exports.ensureTrailingSlash = ensureTrailingSlash;
